<template>
  <div class="news-view">
    <section class="site-hero news-hero" :style="heroStyle">
      <div class="site-hero__inner">
        <h1>企业动态</h1>
        <p v-for="(paragraph, index) in pageIntro" :key="index">{{ paragraph }}</p>
      </div>
    </section>

    <section class="page-section news-section">
      <div class="page-head">
        <div><h2>动态列表</h2></div>
        <div class="search-panel panel-card">
          <el-input
            v-model.trim="keyword"
            clearable
            prefix-icon="el-icon-search"
            placeholder="搜索标题、摘要、标签"
            @clear="handleSearch"
            @input="handleSearch"
          />
        </div>
      </div>

      <div class="page-shell">
        <el-empty v-if="!newsData.length" description="暂无匹配的企业动态" />
        <div v-else class="news-list">
          <a
            v-for="item in newsData"
            :key="item.id"
            class="news-link"
            :href="resolveNewsHref(item)"
            :target="item.articleUrl ? '_blank' : null"
            :rel="item.articleUrl ? 'noopener' : null"
            @click="handleNewsClick($event, item)"
          >
            <article class="news-card panel-card">
              <div class="news-figure">
                <img :src="item.image" :alt="item.title">
              </div>
              <div class="news-body">
                <div class="news-meta">
                  <span>{{ item.category }}</span>
                  <time>{{ item.date }}</time>
                </div>
                <h3>{{ item.title }}</h3>
                <p>{{ item.summary }}</p>
                <div class="news-foot">
                  <div class="tag-list">
                    <span v-for="tag in item.tags" :key="tag">{{ tag }}</span>
                  </div>
                  <i class="el-icon-right"></i>
                </div>
              </div>
            </article>
          </a>
        </div>

        <div v-if="total > pageSize" class="pagination-row">
          <el-pagination
            background
            layout="prev, pager, next"
            :current-page.sync="currentPage"
            :page-size="pageSize"
            :total="total"
            @current-change="loadNews"
          />
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { getNews, getSitePage } from "@/api/website";

export default {
  name: "NewsView",
  data() {
    return {
      keyword: "",
      currentPage: 1,
      pageSize: 10,
      total: 0,
      pageSettings: {
        bannerImage: "/assets/banner.png",
        intro: []
      },
      newsData: []
    };
  },
  computed: {
    pageIntro() {
      return this.pageSettings.intro && this.pageSettings.intro.length
        ? this.pageSettings.intro : [];
    },
    heroStyle() {
      const image = this.pageSettings.bannerImage || "/assets/banner.png";
      return {
        "--page-banner-image": `url("${image}")`
      };
    }
  },
  created() {
    getSitePage("news").then((res) => {
      const data = res.data || {};
      const pageSettings = data.pageSettings || {};
      this.pageSettings = pageSettings.news || this.pageSettings;
    });
    this.loadNews();
  },
  methods: {
    handleSearch() {
      this.currentPage = 1;
      this.loadNews();
    },
    loadNews() {
      getNews({
        keyword: this.keyword,
        pageNo: this.currentPage,
        pageSize: this.pageSize
      }).then((res) => {
        const data = res.data || {};
        this.newsData = data.records || [];
        this.total = data.total || 0;
      });
    },
    resolveNewsHref(item) {
      if (item.articleUrl) {
        return this.normalizeExternalUrl(item.articleUrl);
      }
      return this.$router.resolve({ path: "/news-detail", query: { id: item.id } }).href;
    },
    normalizeExternalUrl(url) {
      const trimmedUrl = String(url || "").trim();
      if (/^[a-z][a-z0-9+.-]*:/i.test(trimmedUrl) || trimmedUrl.startsWith("//")) {
        return trimmedUrl;
      }
      return `https://${trimmedUrl}`;
    },
    handleNewsClick(event, item) {
      if (item.articleUrl) {
        return;
      }
      event.preventDefault();
      this.$router.push({ path: "/news-detail", query: { id: item.id } });
    }
  }
};
</script>

<style lang="less" scoped>
.news-hero {
  background:
    linear-gradient(90deg, rgba(3, 23, 50, 0.94) 0%, rgba(7, 55, 105, 0.74) 48%, rgba(12, 83, 146, 0.34) 100%),
    var(--page-banner-image) center / cover no-repeat;
}

.news-section {
  background: var(--soft-blue);
}

.page-shell {
  width: min(1280px, 100%);
  margin: 0 auto;
}

.search-panel {
  padding: 14px;
}

.news-list {
  display: grid;
  gap: 18px;
}

.news-link {
  display: block;
}

.news-card {
  display: grid;
  grid-template-columns: minmax(260px, 0.34fr) minmax(0, 0.66fr);
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.news-link:hover .news-card {
  transform: translateY(-4px);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8), 0 22px 40px rgba(8, 43, 84, 0.14);
}

.news-figure {
  min-height: 240px;
  background: #f5f9ff;
}

.news-figure img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.news-body {
  display: grid;
  align-content: center;
  gap: 14px;
  padding: clamp(22px, 4vw, 34px);
}

.news-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  color: var(--blue);
  font-size: 13px;
  font-weight: 900;
}

.news-meta time {
  color: var(--muted);
}

.news-body h3 {
  margin: 0;
  font-size: clamp(24px, 3vw, 36px);
  line-height: 1.2;
}

.news-body p {
  margin: 0;
  color: var(--muted);
}

.news-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin-top: 8px;
  color: var(--navy);
}

.pagination-row {
  display: flex;
  justify-content: center;
  margin-top: 28px;
}

@media (max-width: 1040px) {
  .news-card {
    grid-template-columns: 1fr;
  }

  .news-figure {
    min-height: 220px;
  }
}

@media (max-width: 680px) {
  .search-panel {
    padding: 10px;
  }

  .news-figure {
    min-height: 180px;
  }

  .news-body {
    gap: 10px;
    padding: 20px;
  }

  .news-meta {
    font-size: 12px;
  }

  .news-body h3 {
    font-size: 21px;
  }

  .news-body p,
  .news-foot {
    font-size: 14px;
  }
}
</style>
